import { Action, Permission, UserData, UserPermission } from '@dap/memberadmin/types';

interface Props {
  userData: UserData;
  resourceId: Permission['resource'];
  permissions: UserPermission;
  memberKey?: string;
  dataownerKey?: string;
  locationKey?: string;
  /** Union actions from all matching locations (in addition to `locationKey`). */
  locationKeys?: string[];
}

export default function useUserPermissions({
  userData,
  resourceId,
  permissions,
  memberKey,
  dataownerKey,
  locationKey,
  locationKeys,
}: Props): {
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
} {
  if (userData.metadata.superuser) {
    return { canRead: true, canCreate: true, canUpdate: true, canDelete: true };
  }

  const brandActions = permissions?.brands?.find((p) => p.resource === resourceId)?.actions || [];
  const memberActions = memberKey
    ? permissions?.dataowners
        .find((d) => d?.orgKey === memberKey)
        ?.permissions?.find((p) => p.resource === resourceId)?.actions || []
    : [];
  const dataownerActions = dataownerKey
    ? permissions?.dataowners
        .find((d) => d?.orgKey === dataownerKey)
        ?.permissions?.find((p) => p.resource === resourceId)?.actions || []
    : [];
  const locationKeysToCheck = [
    ...(locationKey ? [locationKey] : []),
    ...(locationKeys ?? []),
  ];
  const locationActions =
    locationKeysToCheck.length > 0
      ? permissions?.dataowners
          .flatMap((d) => d.locations)
          .filter((l) => l && locationKeysToCheck.includes(l.orgKey))
          .flatMap(
            (l) => l.permissions?.find((p) => p.resource === resourceId)?.actions || []
          )
      : [];

  const actions = [...brandActions, ...memberActions, ...dataownerActions, ...locationActions];

  return {
    canRead: actions.includes(Action.READ),
    canCreate: actions.includes(Action.CREATE),
    canUpdate: actions.includes(Action.UPDATE),
    canDelete: actions.includes(Action.DELETE),
  };
}
